import {configureStore} from '@reduxjs/toolkit'
import {themeReducer} from './slices/theme'
import teamReducer from './slices/team/teamSlice'
import programReducer from './slices/program/programSlice'
import {default as sidePanelReducer} from './slices/sidePanelSlice'
import filtersReducer from './slices/filtersSlice'
import {persistStore, persistReducer} from 'redux-persist'
import storage from 'redux-persist/lib/storage' // defaults to localStorage
import {
  buildReducer,
  deployReducer,
  featureReducer,
  onboardingDashboardReducer,
  repoReducer,
  securityReducer,
  appsecReducer,
  qaReducer,
  codeAnalysisReducer,
} from './onboardingSlices'
import {buildReducerV2, deployReducerV2, repoReducerV2, securityAnalysisReducerV2} from './onboardingSlicesV2'
import {oldProgramReferenceReducer, programOnboardingReducer} from './programOnboardingSlice'
import programRefreshLoaderReducer from './slices/programRefreshLoaderSlice'
import toastReducer from './slices/toastSlice'
import editStatusReducer from './slices/editStatusSlice'
import {agenticChatReducer} from './slices/agenticChatSlice'
import accessSummaryReducer from './slices/accessSummarySlice'
import tableSettingsReducer from './slices/tableSettingsSlice'

// Team state persistence configuration
const teamPersistConfig = {
  key: 'team',
  storage,
  whitelist: ['teams', 'selectedTeamId'], // Specify which parts of team state to persist
}

// Create persisted team reducer
const persistedTeamReducer = persistReducer(teamPersistConfig, teamReducer)

// Program state persistence configuration
const programPersistConfig = {
  key: 'program',
  storage,
  whitelist: ['programs'], // Only persist the programs array
}

// Create persisted program reducer
const persistedProgramReducer = persistReducer(programPersistConfig, programReducer)

// Table settings persistence configuration
const tableSettingsPersistConfig = {
  key: 'tableSettings',
  storage,
  whitelist: ['settings'], // Persist the settings map
}

// Create persisted table settings reducer
const persistedTableSettingsReducer = persistReducer(tableSettingsPersistConfig, tableSettingsReducer)

export const store = configureStore({
  reducer: {
    onboardingDashboard: onboardingDashboardReducer,
    build: buildReducer,
    deploy: deployReducer,
    buildV2: buildReducerV2, // V2 separate reducer
    deployV2: deployReducerV2, // V2 separate reducer
    repoV2: repoReducerV2, // V2 separate reducer
    securityAnalysisV2: securityAnalysisReducerV2, // V2 separate reducer
    repo: repoReducer,
    appsec: appsecReducer,
    security: securityReducer,
    codeAnalysis: codeAnalysisReducer,
    qa: qaReducer,
    feature: featureReducer,
    theme: themeReducer,
    team: persistedTeamReducer, // Use the persisted reducer
    program: persistedProgramReducer, // Use the persisted reducer for program
    sidePanel: sidePanelReducer,
    filters: filtersReducer,
    programOnboarding: programOnboardingReducer,
    programRefreshLoader: programRefreshLoaderReducer, // Program refresh loader state
    toast: toastReducer, // Toast notifications
    editStatus: editStatusReducer, // Edit Status side panel data
    oldProgramReference: oldProgramReferenceReducer,
    agenticChat: agenticChatReducer, // Agentic chat state
    accessSummary: accessSummaryReducer, // Access delegations summary
    tableSettings: persistedTableSettingsReducer, // Table column settings (visibility + order)
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE', 'persist/PURGE'],
      },
    }),
  devTools: true,
})

// Create persistor
export const persistor = persistStore(store)

// Infer types for use throughout app
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
